import { useTranslation } from 'react-i18next';
import { DetailSection } from '../ui/DetailSection.js';
import { Button } from '../ui/Button.js';
import { useToast } from '../ui/Toast.js';
import { Pill } from '../PillGroup.js';
import type { ProjectRow } from '../../hooks/useProjects.js';
import {
  useComposeDesignEmail,
  useMarkClientApproved,
  useProjectDrafts,
  useProjectReplies,
} from '../../hooks/useProjectOutreach.js';

export interface DesignReviewSectionProps {
  project: ProjectRow;
  divider?: boolean;
}

export function DesignReviewSection({ project, divider = true }: DesignReviewSectionProps) {
  const { t } = useTranslation();
  const toast = useToast();

  const sendDesignMut = useComposeDesignEmail(project.id);
  const markApprovedMut = useMarkClientApproved(project.id);
  const { data: draftsData } = useProjectDrafts(project.id);
  const { data: repliesData } = useProjectReplies(project.id);

  const designReviewDraftIds = new Set(
    (draftsData?.drafts ?? [])
      .filter(d => d.draftType === 'design_review' && d.status === 'sent')
      .map(d => d.id),
  );
  const designReply = [...(repliesData?.replies ?? [])]
    .sort((a, b) => b.receivedAt - a.receivedAt)
    .find(r => r.matchedDraftId !== null && designReviewDraftIds.has(r.matchedDraftId)) ?? null;

  const canMarkApproved = ['in_design', 'client_review'].includes(project.status);

  async function handleMarkApproved() {
    try {
      await markApprovedMut.mutateAsync();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('common.error'));
    }
  }

  return (
    <DetailSection title={t('projects.design.clientReview.title')} divider={divider}>
      {/* Reply indicator — shown above the footer when the client has responded */}
      {designReply !== null && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
          <Pill variant="outline" tone="cyan" size="sm">
            {t('projects.design.clientReview.replied')}
          </Pill>
          <span style={{ fontSize: 'var(--evo-fs-xs)', opacity: 0.6 }}>
            {new Date(designReply.receivedAt).toLocaleDateString()}
          </span>
        </div>
      )}

      {/* Footer actions — single row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--evo-space-2)',
        justifyContent: 'flex-end',
        paddingTop: 'var(--evo-space-4)',
        paddingBottom: 'var(--evo-space-4)',
        flexWrap: 'wrap',
      }}>
        <Button
          variant="outline"
          tone="cyan"
          size="sm"
          loading={sendDesignMut.isPending}
          onClick={() => { sendDesignMut.mutate(); }}
        >
          {designReviewDraftIds.size > 0
            ? t('projects.design.clientReview.resend')
            : t('projects.design.clientReview.send')}
        </Button>
        <Button
          variant="solid"
          tone="cyan"
          size="sm"
          disabled={!canMarkApproved}
          loading={markApprovedMut.isPending}
          onClick={() => { void handleMarkApproved(); }}
        >
          {t('projects.design.clientReview.markApproved')}
        </Button>
      </div>
    </DetailSection>
  );
}
